import { type SearchResult, useApiV1Omnisearch } from 'api';
import { useState } from 'react';
import { Autocomplete, styled, useBreakpoint } from 'ui';
import { Search } from 'ui/icons';

import { useDebounce } from '../../../debounce';

const OptionLink = styled('a')({
  color: 'inherit',
  textDecoration: 'none',
});

type OmnisearchAutocompleteProps = {
  onOptionSelect: () => void;
  autocompleteRef: React.RefObject<HTMLDivElement>;
};

const DEBOUNCE_DELAY = 300;

export const OmnisearchAutocomplete = ({ onOptionSelect, autocompleteRef }: OmnisearchAutocompleteProps) => {
  const { isMobile } = useBreakpoint();
  const [value, setValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  const debouncedSetValue = useDebounce((newValue: string) => {
    setDebouncedValue(newValue);
  }, DEBOUNCE_DELAY);

  const { data, isLoading } = useApiV1Omnisearch(
    {
      q: debouncedValue,
    },
    {
      query: {
        enabled: debouncedValue.length > 1,
      },
    }
  );

  const options = data?.results || [];

  const handleChange = (_event: React.SyntheticEvent, newValue: SearchResult | null): void => {
    if (!newValue) {
      return;
    }
    onOptionSelect();

    window.open(newValue.url, '_self');
  };

  return (
    <Autocomplete<SearchResult>
      ref={autocompleteRef}
      inputValue={value}
      fullWidth
      autoFocus={true}
      autoSelect
      size={isMobile ? 'medium' : 'small'}
      autoHighlight
      loading={isLoading}
      placeholder={isMobile ? 'Search' : 'Ctrl/Cmd + K to search'}
      noOptionsText={value ? 'No results found' : 'Type to search'}
      startAdornment={<Search />}
      options={options}
      onInputChange={(_event, newValue) => {
        setValue(newValue);
        debouncedSetValue(newValue);
      }}
      onChange={handleChange}
      getOptionLabel={(option) => option.title || ''}
      renderOption={(props, option) => (
        <OptionLink href={option.url} key={props.key}>
          <li {...props}>
            {option.icon} {option.title}
          </li>
        </OptionLink>
      )}
      isOptionEqualToValue={(option, value) => value === undefined || option?.title === (value?.title ?? value)}
      sx={{
        minWidth: isMobile ? '100%' : 260,
      }}
    />
  );
};
